<template>
  <collapsible-section class="_pt0 _pb0" :collapsed="internalCollapsed">
    <template #headerText>
      <p class="color-leaf">Privacy Address</p>
    </template>
    <template #collapsibleSection>
      <async-loader v-if="memberDetailsLoading"/>
      <div v-else container>
        <div grid>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="First Name:"
              :data="internalValue.firstName"
            />
            <app-input
              v-else
              :required='isUSorForeignRequired'
              :disabled="memberLocked"
              v-model="$v.internalValue.firstName.$model"
              :errorMessage="firstNameError"
              :invalid="$v.internalValue.firstName.$invalid"
              valueKey="firstName"
              label="First Name:"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Middle Initial:"
              :data="internalValue.midInitial"
            />
            <app-input
              v-else
              :disabled="memberLocked"
              v-model="$v.internalValue.midInitial.$model"
              :errorMessage="midInitialError"
              :invalid="$v.internalValue.midInitial.$invalid"
              valueKey="midInitial"
              label="Middle Initial:"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Last Name:"
              :data="internalValue.lastName"
            />
            <app-input
              v-else
              :required='isUSorForeignRequired'
              :disabled="memberLocked"
              v-model="$v.internalValue.lastName.$model"
              :errorMessage="lastNameError"
              :invalid="$v.internalValue.lastName.$invalid"
              valueKey="lastName"
              label="Last Name:"
              @input="emitInternalValue"
            />
          </div>
        </div>
        <div grid class="_mtxs">
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Located In US?:"
              :data="internalValue.locatedInUS"
            />
            <app-select
              v-else
              :required='anyPartOfPrivacyNameFilledOut'
              :disabled="memberLocked"
              valueKey="locatedInUS"
              :options="yesNoOptions"
              label="Located In US?"
              @input="emitInternalValue"
              v-model="$v.internalValue.locatedInUS.$model"
              :errorMessage="locatedInUSError"
              :invalid="$v.internalValue.locatedInUS.$invalid"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Address Line 1:"
              :data="internalValue.line1"
            />
            <app-input
              v-else
              :required='isUSorForeignRequired'
              :disabled="memberLocked"
              v-model="$v.internalValue.line1.$model"
              :errorMessage="address1Error"
              :invalid="$v.internalValue.line1.$invalid"
              valueKey="line1"
              label="Address Line 1:"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Address Line 2:"
              :data="internalValue.line2"
            />
            <app-input
              v-else
              :disabled="memberLocked"
              v-model="$v.internalValue.line2.$model"
              :errorMessage="address2Error"
              :invalid="$v.internalValue.line2.$invalid"
              valueKey="line2"
              label="Address Line 2:"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Address Line 3:"
              :data="internalValue.line3"
            />
            <app-input
              v-else
              :disabled="memberLocked"
              v-model="$v.internalValue.line3.$model"
              :errorMessage="address3Error"
              :invalid="$v.internalValue.line3.$invalid"
              valueKey="line3"
              label="Address Line 3:"
              @input="emitInternalValue"
            />
          </div>
        </div>
        <div grid class="_mtxs">
          <div column>
            <labeled-data
              v-if="readOnly"
              label="Zip Code:"
              :data="internalValue.zipCode"
            />
            <app-input
              v-else
              :required='isUSRequired'
              v-model="$v.internalValue.zipCode.$model"
              :invalid="$v.internalValue.zipCode.$invalid"
              :errorMessage="zipError"
              :disabled="memberLocked"
              valueKey="zipCode"
              label="Zip Code:"
              type="number"
              pattern="[0-9]"
              @input="zipCodeUpdated"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="City:"
              :data="internalValue.city"
            />
            <app-input
              v-else
              :required='isUSRequired'
              :disabled="memberLocked"
              v-model="$v.internalValue.city.$model"
              :errorMessage="cityError"
              :invalid="$v.internalValue.city.$invalid"
              valueKey="city"
              label="City:"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="State:"
              :data="internalValue.state"
            />
            <app-select
              v-else
              :required='isUSRequired'
              :disabled="memberLocked"
              v-model="$v.internalValue.state.$model"
              :invalid="$v.internalValue.state.$invalid"
              :errorMessage="stateError"
              valueKey="state"
              label="State:"
              :options="stateOptions"
              @input="emitInternalValue"
            />
          </div>
          <div column>
            <labeled-data
              v-if="readOnly"
              label="County:"
              :data="internalValue.county"
            />
            <app-input
              v-else
              :disabled="memberLocked"
              v-model="$v.internalValue.county.$model"
              :errorMessage="countyError"
              :invalid="$v.internalValue.county.$invalid"
              valueKey="county"
              label="County:"
              @input="emitInternalValue"
            />
          </div>
        </div>
      </div>
    </template>
  </collapsible-section>
</template>

<script>
  import AppInput from '@atoms/Input'
  import AppSelect from '@atoms/Select'
  import CollapsibleSection from '@sections/CollapsibleSection'
  import AsyncLoader from '@molecules/AsyncLoader'
  import LabeledData from '@molecules/LabeledData'

  import ZipCodeHelper from '@mixins/ZipCodeHelper'
  import SelectOptions from '@mixins/SelectOptions'
  import autoExpandable from '@mixins/autoExpandable'
  import formReady from '@mixins/formReady'
  import formattable from '@mixins/formattable'

  import { maxLength, numeric, requiredIf } from 'vuelidate/lib/validators'
  import { updateValues, foreignIndicatorToLocatedInUS, nameAlphaNumeric, fiveOrNineDigitsLong } from '@/utils'

  export default {
    mixins: [
      formattable,
      SelectOptions,
      autoExpandable,
      formReady,
      ZipCodeHelper
    ],
    components: {
      CollapsibleSection,
      AppInput,
      AppSelect,
      AsyncLoader,
      LabeledData
    },
    props: {
      readOnly: {
        type: Boolean,
        default: false
      },
      collapsed: {
        type: Boolean,
        default: true
      },
      memberDetailsLoading: {
        type: Boolean,
        default: false
      },
      memberLocked: Boolean,
      value: {
        type: Object,
        default: function() {
          return {
            firstName: '',
            midInitial: '',
            lastName: '',
            line1: '',
            line2: '',
            line3: '',
            locatedInUS: '',
            zipCode: '',
            city: '',
            state: '',
            county: ''
          }
        }
      }
    },
    data() {
      return {
        internalValue: {
          firstName: this.value.firstName || '',
          midInitial: this.value.midInitial || '',
          lastName: this.value.lastName || '',
          line1: this.value.line1 || '',
          line2: this.value.line2 || '',
          line3: this.value.line3 || '',
          locatedInUS: foreignIndicatorToLocatedInUS(this.value.foreignIndicator, this.value.locatedInUS),
          zipCode: this.value.zipCode || '',
          city: this.value.city || '',
          state: this.value.state || '',
          county: this.value.county || ''
        }
      }
    },
    validations: {
      internalValue: {
        locatedInUS: {
          required: requiredIf(function() {
            return this.anyPartOfPrivacyNameFilledOut
          })
        },
        zipCode: {
          required: requiredIf(function() {
            return this.isUSRequired
          }),
          numeric,
          fiveOrNineDigitsLong
        },
        firstName: {
          required: requiredIf(function() {
            return this.isUSorForeignRequired
          }),
          maxLength: maxLength(20),
          nameAlphaNumeric
        },
        midInitial: {
          maxLength: maxLength(1)
        },
        lastName: {
          required: requiredIf(function() {
            return this.isUSorForeignRequired
          }),
          maxLength: maxLength(35),
          nameAlphaNumeric
        },
        line1: {
          required: requiredIf(function() {
            return this.isUSorForeignRequired
          }),
          maxLength: maxLength(48)
        },
        line2: {
          maxLength: maxLength(48)
        },
        line3: {
          maxLength: maxLength(48)
        },
        city: {
          required: requiredIf(function() {
            return this.isUSRequired
          }),
          maxLength: maxLength(32)
        },
        state: {
          required: requiredIf(function() {
            return this.isUSRequired
          })
        },
        county: {
          maxLength: maxLength(32)
        }
      }
    },
    computed: {
      anyPartOfPrivacyNameFilledOut() {
        let anythingHasValue = false
        Object.values(this.internalValue).forEach(value => {
          if (value) {
            anythingHasValue = true
          }
        })

        return anythingHasValue
      },
      isUSRequired() {
        return this.anyPartOfPrivacyNameFilledOut && this.internalValue.locatedInUS === 'yes'
      },
      isUSorForeignRequired() {
        return this.anyPartOfPrivacyNameFilledOut && this.internalValue.locatedInUS !== ''
      },
      firstNameError() {
        if (!this.$v.internalValue.firstName.required) {
          return 'required'
        } else if (!this.$v.internalValue.firstName.maxLength) {
          return 'character limit exceeded'
        } else if (!this.$v.internalValue.firstName.nameAlphaNumeric) {
          return 'only letters, spaces, numbers, *, and - allowed'
        }
        return null
      },
      midInitialError() {
        if (!this.$v.internalValue.midInitial.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      lastNameError() {
        if (!this.$v.internalValue.lastName.required) {
          return 'required'
        } else if (!this.$v.internalValue.lastName.maxLength) {
          return 'character limit exceeded'
        } else if (!this.$v.internalValue.lastName.nameAlphaNumeric) {
          return 'only letters, spaces, numbers, *, and - allowed'
        }
        return null
      },
      locatedInUSError() {
        if (!this.$v.internalValue.locatedInUS.required) {
          return 'required'
        }
        return null
      },
      zipError() {
        if (!this.$v.internalValue.zipCode.required) {
          return 'required'
        }

        let hasValue = this.$v.internalValue.zipCode.$model

        if (
          hasValue &&
          (this.$v.internalValue.zipCode.$dirty ||
            this.$v.internalValue.zipCode.$model.length !== 0)
        ) {
          if (!this.$v.internalValue.zipCode.numeric) {
            return 'must be numeric'
          } else if (!this.$v.internalValue.zipCode.fiveOrNineDigitsLong) {
            return '5 or 9 digits long'
          }
        }
        return null
      },
      address1Error() {
        if (!this.$v.internalValue.line1.required) {
          return 'required'
        }

        if (!this.$v.internalValue.line1.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      address2Error() {
        if (!this.$v.internalValue.line2.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      address3Error() {
        if (!this.$v.internalValue.line3.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      cityError() {
        if (!this.$v.internalValue.city.required) {
          return 'required'
        }

        if (!this.$v.internalValue.city.maxLength) {
          return 'character limit exceeded'
        }
        return null
      },
      stateError() {
        if (!this.$v.internalValue.state.required) {
          return 'required'
        }
        return null
      },
      countyError() {
        if (!this.$v.internalValue.county.maxLength) {
          return 'character limit exceeded'
        }
        return null
      }
    },
    methods: {
      emitInternalValue() {
        this.$emit('input', this.internalValue)
      },
      zipCodeUpdated() {
        if (this.$v.internalValue.zipCode.$invalid) {
          return
        }

        this.suggestLocationFromZipCode(this.internalValue.zipCode)
          .then((suggestion) => {
            if (!suggestion) {
              return
            }

            if (suggestion.city) {
              this.internalValue.city = suggestion.city
            }

            if (suggestion.state) {
              this.internalValue.state = suggestion.state
            }

            if (suggestion.county) {
              this.internalValue.county = suggestion.county
            }

            this.emitInternalValue()
          })
      }
    },
    watch: {
      value(newValue, oldValue) {
        updateValues(this.internalValue, newValue)

        // even though we called updateValues, we still need the transformation on locatedInUS from foreignIndicatorToLocatedInUS
        this.internalValue.locatedInUS = foreignIndicatorToLocatedInUS(newValue.foreignIndicator, newValue.locatedInUS)
      }
    }
  }
</script>
